import React, { useState } from "react";
import {
  BellSimple, ShieldCheck, Palette, Stack, TextT, Tag, Sliders, CreditCard, IdentificationBadge, Timer,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import SettingsNav from "./settings/SettingsNav";
import NotificationsPanel from "./settings/NotificationsPanel";
import SecurityPanel from "./settings/SecurityPanel";
import RolesPanel from "./settings/RolesPanel";
import BrandingPanel from "./settings/BrandingPanel";
import CustomFieldsManager from "./settings/CustomFieldsManager";
import FormFieldsManager from "./settings/FormFieldsManager";
import StatusesManager from "./settings/StatusesManager";
import MastersPanel from "./settings/MastersPanel";
import SlaPanel from "./settings/SlaPanel";
import SubscriptionPanel from "./settings/SubscriptionPanel";

const SECTIONS = [
  { key: "notifications", label: "Notifications",   group: "Account",   icon: BellSimple },
  { key: "security",      label: "Security",        group: "Account",   icon: ShieldCheck },
  { key: "roles",         label: "Roles",           group: "Workspace", icon: IdentificationBadge },
  { key: "branding",      label: "Branding",        group: "Workspace", icon: Palette },
  { key: "custom-fields", label: "Custom Fields",   group: "Workspace", icon: Stack },
  { key: "form-fields",   label: "Form Fields",     group: "Workspace", icon: TextT },
  { key: "statuses",      label: "Ticket Statuses", group: "Workspace", icon: Tag },
  { key: "masters",       label: "Masters",         group: "Workspace", icon: Sliders },
  { key: "sla",          label: "SLA",             group: "Workspace", icon: Timer },
  { key: "subscription",  label: "Subscription",    group: "Workspace", icon: CreditCard },
];

function SectionContent({ sectionKey, user, setUser }) {
  switch (sectionKey) {
    case "notifications": return <NotificationsPanel user={user} setUser={setUser} />;
    case "security":      return <SecurityPanel user={user} setUser={setUser} />;
    case "roles":         return <RolesPanel />;
    case "branding":      return <BrandingPanel user={user} setUser={setUser} />;
    case "custom-fields": return <CustomFieldsManager />;
    case "form-fields":   return <FormFieldsManager />;
    case "statuses":      return <StatusesManager />;
    case "masters":       return <MastersPanel />;
    case "sla":          return <SlaPanel />;
    case "subscription":  return <SubscriptionPanel />;
    default:              return null;
  }
}

export default function Settings() {
  const { user, setUser } = useAuth();
  const isSuperAdmin = user.role === "super_admin";
  const [section, setSection] = useState("notifications");

  const header = (
    <div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold">
        {isSuperAdmin ? "Administration" : "Account"}
      </div>
      <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1">Settings</h1>
      <p className="text-sm text-slate-500 mt-1">
        {isSuperAdmin ? "Manage your account, branding, and workspace configuration." : "Manage your notifications and sign-in."}
      </p>
    </div>
  );

  // Non-super-admin: simple single column (Notifications + Security), no rail.
  if (!isSuperAdmin) {
    return (
      <div className="max-w-2xl space-y-8" data-testid="settings-page">
        {header}
        <NotificationsPanel user={user} setUser={setUser} />
        <SecurityPanel user={user} setUser={setUser} />
      </div>
    );
  }

  // Super-admin: two-pane shell.
  return (
    <div className="max-w-[1100px] space-y-6" data-testid="settings-page">
      {header}
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-10">
        <SettingsNav sections={SECTIONS} active={section} onSelect={setSection} />
        <div className="flex-1 min-w-0">
          <SectionContent sectionKey={section} user={user} setUser={setUser} />
        </div>
      </div>
    </div>
  );
}
